import { api } from '../../shared/api/client'

// Поля единого справочника attribute_values (модель атрибутов 2026-07-20).
// profession ретайрен (значения в архиве), оставлен в типе для чтения истории.
export type SeoDictionaryField =
  | 'type' | 'category' | 'gender' | 'age' | 'occasion' | 'brand'
  | 'recipient_status' | 'persona' | 'legal_status'
  | 'season' | 'seasonal' | 'profession'
export type NewitemMarketplace = 'wb' | 'ozon'
export type RecipeInputKind = 'pdf' | 'sheet_12' | 'jpg' | 'png'

export interface BaseCategory {
  category_id: number
  prefix: string
  description: string
}

export interface SeoDictionaryItem {
  id: number
  field: SeoDictionaryField
  value: string
  sort: number
}

export interface NewitemPrice {
  category_id: number
  marketplace: NewitemMarketplace
  price: number
  updated_at?: string
  prefix?: string
  category_description?: string
}

export interface PipelineSetting {
  setting_key: string
  setting_value: string
  comment?: string | null
}

export interface CategoryRecipe {
  category_id: number
  prefix?: string
  category_description?: string
  input_kind: RecipeInputKind
  needs_cut_circles: boolean
  needs_3d_render: boolean
  funnel_template_set?: string | null
  print_sheet_format?: string | null
  produce_plt: boolean
  produce_stl: boolean
  auto_generate: boolean
  options?: Record<string, unknown> | null
}

export interface CategoryTextTemplates {
  category_id: number
  prefix?: string
  title_template?: string | null
  description_template?: string | null
}

function unwrap<T>(response: any, fallback: T): T {
  return response.data?.data ?? response.data ?? fallback
}

export const pipelineRulesAPI = {
  async getCategories(): Promise<BaseCategory[]> {
    const response = await api.get('/categories')
    const data = unwrap<BaseCategory[]>(response, [])
    return Array.isArray(data) ? data : []
  },

  async getSeoDictionaries(field?: SeoDictionaryField): Promise<SeoDictionaryItem[]> {
    const response = await api.get('/seo-dictionaries', { params: field ? { field } : undefined })
    const data = unwrap<SeoDictionaryItem[]>(response, [])
    return Array.isArray(data) ? data : []
  },

  async saveSeoDictionary(data: {
    id?: number
    field: SeoDictionaryField
    value: string
    sort: number
  }): Promise<SeoDictionaryItem> {
    const response = await api.post('/seo-dictionaries', data)
    return unwrap<SeoDictionaryItem>(response, response.data)
  },

  async deleteSeoDictionary(id: number): Promise<void> {
    await api.delete(`/seo-dictionaries/${id}`)
  },

  async importSeoDictionariesFromDesigns(): Promise<{ imported: number; skipped: number; fields: string[] }> {
    const response = await api.post('/seo-dictionaries/import-from-designs')
    return unwrap(response, { imported: 0, skipped: 0, fields: [] })
  },

  async getNewitemPrices(categoryId?: number): Promise<NewitemPrice[]> {
    const response = await api.get('/newitem-prices', {
      params: categoryId ? { category_id: categoryId } : undefined,
    })
    const data = unwrap<NewitemPrice[]>(response, [])
    return Array.isArray(data) ? data : []
  },

  async saveNewitemPrice(data: {
    category_id: number
    marketplace: NewitemMarketplace
    price: number
  }): Promise<NewitemPrice> {
    const response = await api.put('/newitem-prices', data)
    return unwrap<NewitemPrice>(response, response.data)
  },

  async getPipelineSettings(): Promise<PipelineSetting[]> {
    const response = await api.get('/pipeline-settings')
    const data = unwrap<PipelineSetting[]>(response, [])
    return Array.isArray(data) ? data : []
  },

  async savePipelineSetting(data: PipelineSetting): Promise<PipelineSetting> {
    const response = await api.put('/pipeline-settings', data)
    return unwrap<PipelineSetting>(response, response.data)
  },

  async getCategoryRecipe(categoryId: number): Promise<CategoryRecipe> {
    const response = await api.get(`/categories/${categoryId}/recipe`)
    return unwrap<CategoryRecipe>(response, response.data)
  },

  async saveCategoryRecipe(categoryId: number, data: CategoryRecipe): Promise<CategoryRecipe> {
    const response = await api.put(`/categories/${categoryId}/recipe`, data)
    return unwrap<CategoryRecipe>(response, response.data)
  },

  async getCategoryTextTemplates(categoryId: number): Promise<CategoryTextTemplates> {
    const response = await api.get(`/categories/${categoryId}/text-templates`)
    return unwrap<CategoryTextTemplates>(response, response.data)
  },

  async saveCategoryTextTemplates(
    categoryId: number,
    data: Pick<CategoryTextTemplates, 'title_template' | 'description_template'>,
  ): Promise<CategoryTextTemplates> {
    const response = await api.put(`/categories/${categoryId}/text-templates`, data)
    return unwrap<CategoryTextTemplates>(response, response.data)
  },
}
